import { useRequestSocialMediaProfileAIAnalysis } from "@entities/influencer";
import { ToastService } from "@shared/lib/services";
import { SocialMediaJobStatuses } from "@shared/lib/types/influencer";
import { ISocialMediaInfluencerV2 } from "@shared/lib/types/socialMediaInfluencerV2";
import { Button } from "@shared/ui";
import { checkCreatorProfileFitCheckRetry, profileJobErrorToText } from "@widgets/CampaignBrief/ui/CampaignBriefTable/lib/aiFitCheckUtils";
import dayjs from "dayjs";
import { AlertCircle, BrainCircuit, CheckCircle2 as CheckCircle, Loader2 as Loader, ShieldCheck, Sparkles } from "lucide-react";
import { useEffect } from "react";
import styles from "./styles.module.scss";

interface SocialMediaAIAnalysisProps {
  socialMediaInfluencer?: ISocialMediaInfluencerV2;
}

export function SocialMediaAIAnalysis({
  socialMediaInfluencer,
}: SocialMediaAIAnalysisProps) {
  const {
    requestProfileAnalysis,
    isRequestProfileAnalysisLoading,
    isRequestProfileAnalysisSuccess,
    requestProfileAnalysisError
  } = useRequestSocialMediaProfileAIAnalysis();

  const aiAnalysis = socialMediaInfluencer?.socialMedia?.creatorProfileJob?.creatorProfile?.aiAnalysis;
  const job = socialMediaInfluencer?.socialMedia?.creatorProfileJob;
  const isUploading = job ? job?.status !== SocialMediaJobStatuses.Completed && job?.status !== SocialMediaJobStatuses.Failed : false;
  const isFailed = job?.status === SocialMediaJobStatuses.Failed;
  const isSuccess = job?.status === SocialMediaJobStatuses.Completed;

  useEffect(() => {
    if (isRequestProfileAnalysisSuccess) {
      ToastService.success("Request for AI profile analysis has been sent successfully!");
    } else if (requestProfileAnalysisError) {
      ToastService.error(requestProfileAnalysisError?.message);
    }
  }, [isRequestProfileAnalysisSuccess, requestProfileAnalysisError]);

  const runAiAnalysis = () => {
    requestProfileAnalysis(socialMediaInfluencer?.socialMedia?._id || "");
  };

  return (
    <div className={styles.aiAnalysisSection}>
      <div className={styles.aiAnalysisHeader}>
        <div className={styles.aiAnalysisHeaderLeft}>
          <Sparkles size={16} className={styles.sparklesIcon} />
          <span className={styles.aiAnalysisTitle}>AI Profile Analysis</span>
        </div>
        {isSuccess && (
          <span className={styles.updatedBadge}>Updated {dayjs(socialMediaInfluencer?.socialMedia?.creatorProfileJob?.completedAt).format("DD MMM YYYY")}</span>
        )}
      </div>

      <div className={styles.aiAnalysisContent}>
        {isUploading ? (
          <div className={styles.processingState}>
            <Loader size={28} className={styles.processingLoader} />
            <p className={styles.processingMessage}>Analyzing content quality and brand safety...</p>
          </div>
        ) : isFailed ? (
          <div className={styles.idleState}>
            <div className={styles.idleIconContainer} style={{ background: "#fef2f2" }}>
              <AlertCircle size={24} style={{ color: "#ef4444" }} />
            </div>
            <h5 style={{ fontSize: "14px", fontWeight: "700", color: "#1a1f36", marginBottom: "8px" }}>
              Analysis Failed
            </h5>
            <p className={styles.idleDescription} style={{ marginBottom: "16px" }}>
              {job.failure?.code ? profileJobErrorToText[job?.failure?.code] : ""}
            </p>
            <Button
              onClick={runAiAnalysis}
              className={styles.runAnalysisButton}
              loading={isRequestProfileAnalysisLoading}
              disabled={!checkCreatorProfileFitCheckRetry(job?.failure?.code)}
            >
              <Sparkles size={14} />
              Retry Analysis
            </Button>
          </div>
        ) : !aiAnalysis ? (
          <div className={styles.idleState}>
            <div className={styles.idleIconContainer}>
              <BrainCircuit size={24} className={styles.idleIcon} />
            </div>
            <p className={styles.idleDescription}>
              Run a deep-dive analysis on content quality, brand safety, and
              audience authenticity.
            </p>
            <Button
              onClick={runAiAnalysis}
              className={styles.runAnalysisButton}
              loading={isRequestProfileAnalysisLoading}
            >
              <Sparkles size={14} />
              Run Analysis
            </Button>
          </div>
        ) : null}

        {!isUploading && !isFailed && aiAnalysis && (
          <>
            {/* Summary */}
            <div className={styles.summaryCard}>
              <p className={styles.summaryText}>
                <span className={styles.summaryLabel}>Summary:</span>{" "}
                {aiAnalysis.creator_matchmaking_description || aiAnalysis.overall_brand_fit?.brand_fit_summary}
              </p>
            </div>

            {/* Categories */}
            {aiAnalysis.overall_brand_fit?.best_fit_brand_categories && aiAnalysis.overall_brand_fit.best_fit_brand_categories.length > 0 && (
              <div className={styles.categoriesSection}>
                <h5 className={styles.sectionTitle}>Best Categories</h5>
                <div className={styles.categoriesList} style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: "6px" }}>
                  {aiAnalysis.overall_brand_fit.best_fit_brand_categories.map((category: string, idx: number) => (
                    <span key={idx} className={styles.categoryTag}>{category}</span>
                  ))}
                </div>
                {aiAnalysis.overall_brand_fit.brand_fit_summary && (
                  <p className={styles.categoryDescription} style={{ marginTop: 4 }}>
                    {aiAnalysis.overall_brand_fit.brand_fit_summary}
                  </p>
                )}
              </div>
            )}

            {/* Brand Safety & Authenticity */}
            <div className={styles.safetyGrid}>
              <div className={styles.safetyItem}>
                <h5 className={styles.sectionTitle}>Brand Safety</h5>
                <div className={styles.safetyContent}>
                  <ShieldCheck size={16} className={styles.safetyIcon} />
                  <div className={styles.safetyText}>
                    <span className={styles.safetyLabel}>
                      {aiAnalysis.overall_safety?.overall_safety_score !== undefined
                        ? `${aiAnalysis.overall_safety.overall_safety_score}/100 Score`
                        : "N/A"}
                    </span>
                    {aiAnalysis.overall_safety?.safety_notes && (
                      <span className={styles.safetyDescription}>
                        {aiAnalysis.overall_safety.safety_notes.join(" ")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className={styles.safetyItem}>
                <h5 className={styles.sectionTitle}>Authenticity</h5>
                <div className={styles.safetyContent}>
                  <CheckCircle size={16} className={styles.authenticityIcon} />
                  <div className={styles.safetyText}>
                    <span className={styles.safetyLabel}>
                      {aiAnalysis.overall_scores?.authenticity_score !== undefined
                        ? `${aiAnalysis.overall_scores.authenticity_score}/100 Score`
                        : "N/A"}
                    </span>
                    {aiAnalysis.overall_creator_persona?.authenticity_summary && (
                      <span className={styles.safetyDescription}>
                        {aiAnalysis.overall_creator_persona.authenticity_summary}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Note */}
            <div className={styles.aiAnalysisFooter}>
              <span>Analysis based on recent posts/reels.</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
